import React, {useEffect, useState} from 'react';
import AddFreeformChoiceUI from '../view/AddFreeformChoiceUI';
import AddFreeformChoiceStore from '../store/AddFreeformChoiceStore';
import {inject, observer} from 'mobx-react';
import {
  LessonContentItem,
  LessonContentList,
} from '../../../../data/models/LessonContent/LessonContentList';
import {
  getChoiceOptions,
  getNodeParent,
  getSourceParent,
  nodeIsAVideo,
} from '../utlis';
import {
  IMAGE_NODE_TYPE,
  LessonContentBase,
  LessonContentChallengeBranchQuestion,
  LessonContentImageSource,
  LessonContentVideo,
} from '../../../../data/models/LessonContent/LessonContentBase';
import {useParams} from 'react-router-dom';
import {getNodeById} from '../../../TapyblPlayer/lessonContentParser';
import {appText} from '../../../../assets/text/appText';

interface Props {
  addFreeformChoiceStore?: AddFreeformChoiceStore;
  lessonContent: LessonContentList;
}

const AddFreeformChoiceContainer = inject('addFreeformChoiceStore')(
  observer(({addFreeformChoiceStore, lessonContent}: Props) => {
    const [currentSourceNode, setCurrentSourceNode] =
      useState<LessonContentItem>();

    const {organizationId, lessonId} = useParams();

    useEffect(() => {
      if (!addFreeformChoiceStore!.wizardShown) return;
      if (addFreeformChoiceStore!.parentNodeId) {
        const sourceNode = getSourceParent(
          addFreeformChoiceStore!.parentNodeId,
          lessonContent,
        );
        setCurrentSourceNode(sourceNode);
      }
      if (addFreeformChoiceStore!.inEditMode) {
        const sourceNode = getSourceParent(
          addFreeformChoiceStore!.currentNode?.internalId || '',
          lessonContent,
        );
        setCurrentSourceNode(sourceNode);
      }
    }, [
      addFreeformChoiceStore!.parentNodeId,
      addFreeformChoiceStore!.wizardShown,
    ]);

    const getVideoSource = () => {
      if (
        currentSourceNode &&
        nodeIsAVideo(currentSourceNode.data.ivNodeType)
      ) {
        return (currentSourceNode.data as LessonContentVideo).contentUrl;
      }
    };

    const getImageSource = () => {
      if (
        currentSourceNode &&
        currentSourceNode.data.ivNodeType === IMAGE_NODE_TYPE
      ) {
        return (currentSourceNode.data as LessonContentImageSource).contentUrl;
      }
    };

    const getVideoSourceTimeEnd = () => {
      if (
        currentSourceNode &&
        nodeIsAVideo(currentSourceNode.data.ivNodeType)
      ) {
        return (currentSourceNode.data as LessonContentVideo).endInSeconds;
      }
    };

    const onSave = (options: LessonContentBase[]) => {
      if (organizationId && lessonId) {
        addFreeformChoiceStore!.saveNodes(organizationId, lessonId, options);
      }
    };

    const getBranchOptions = () => {
      if (
        addFreeformChoiceStore!.parentNodeId &&
        addFreeformChoiceStore!.currentNode
      ) {
        const options = getChoiceOptions(
          addFreeformChoiceStore!.parentNodeId,
          lessonContent,
        );
        if (
          addFreeformChoiceStore!.currentNode &&
          addFreeformChoiceStore!.nodeToReplaceId
        ) {
          return [
            ...options.filter(
              x => x.internalId !== addFreeformChoiceStore!.nodeToReplaceId,
            ),
            {...addFreeformChoiceStore!.currentNode},
          ];
        } else if (addFreeformChoiceStore!.currentNode) {
          return [...options, {...addFreeformChoiceStore!.currentNode}];
        }
        return [...options];
      } else if (addFreeformChoiceStore!.currentNode) {
        return getChoiceOptions(
          getNodeParent(
            addFreeformChoiceStore!.currentNode!.internalId,
            lessonContent,
          ),
          lessonContent,
        );
      }
      return [];
    };

    const getParentQuestion = () => {
      if (
        addFreeformChoiceStore!.parentNodeId &&
        addFreeformChoiceStore!.currentNode
      ) {
        const parent = getNodeById(
          addFreeformChoiceStore!.parentNodeId,
          lessonContent,
        );
        if (parent)
          return (parent.data as LessonContentChallengeBranchQuestion)
            ?.question;
      } else if (addFreeformChoiceStore!.currentNode) {
        const parentId = getNodeParent(
          addFreeformChoiceStore!.currentNode!.internalId,
          lessonContent,
        );
        const parent = getNodeById(parentId, lessonContent);
        if (parent)
          return (parent.data as LessonContentChallengeBranchQuestion)
            ?.question;
      }
      return appText.sampleQuestion;
    };

    return addFreeformChoiceStore!.wizardShown ? (
      <AddFreeformChoiceUI
        onClose={() => addFreeformChoiceStore!.closeWizard()}
        videoSource={getVideoSource()}
        videoEndTime={getVideoSourceTimeEnd()}
        currentNodeId={addFreeformChoiceStore!.currentNode?.internalId || ''}
        saveChanges={onSave}
        branchOptions={getBranchOptions()}
        imageSource={getImageSource()}
        isLoading={addFreeformChoiceStore!.isLoading}
        question={getParentQuestion()}
      />
    ) : null;
  }),
);

export default AddFreeformChoiceContainer;
